import { setProcedures, configureIguazuRPC } from 'iguazu-rpc';
import { configureIguazuREST } from 'iguazu-rest';
import merge from 'deepmerge';
import { v4 as uuidv4 } from 'uuid';
import { createTimeoutFetch } from '@americanexpress/fetch-enhancers';
import imageViewerDocUpload from '@americanexpress/iguazu-resources/imageViewerDocUpload';

import createCacheHash from './utils/createCacheHash';
import createErrorResponse from './utils/createErrorResponse';
import memberProducts from './resources/memberProducts';
import memberPreferences from './resources/memberPreferences';

// create
const CREATE_BANKING_SAVINGS_ACCOUNT_V1_FUNCTION =
  'createBankingSavingsAccountV1';
const CREATE_MEMBER_PREFERENCES_V1_FUNCTION = 'createMemberPreferencesV1';

// read
const READ_BANKING_APPLICATION_DECISION_V1_FUNCTION =
  'readBankingApplicationDecisionV1';
const READ_BANKING_FINANCIAL_INSTITUTION_LOOKUP_V1_FUNCTION =
  'readBankingFinancialInstitutionLookupV1';
const READ_BANKING_SAVINGS_ACCOUNTS_V2_FUNCTION =
  'readBankingSavingsAccountsV2';
const READ_BUSINESS_CHECKING_APPLICATION_V1_FUNCTION =
  'readBusinessCheckingApplicationV1';
const READ_CUSTOMER_PRODUCTS_V2_FUNCTION = 'readCustomerProductsV2';
const READ_CUSTOMER_PRODUCT_DETAILS_FUNCTION = 'readCustomerProductDetailsV1';
const READ_MEMBER_PREFERENCES_V1_FUNCTION = 'readMemberPreferencesV1';
const READ_USER_SESSION_V1_FUNCTION = 'readUserSessionV1';
const READ_USER_MFA_V1_FUNCTION = 'readUserMultiFactorAuthenticationV1';

// resources
const MEMBER_PRODUCTS_RESOURCE = 'memberProducts';
const MEMBER_PREFERENCES_RESOURCE = 'memberPreferences';

const defaultResponseModifier = async (response) => {
  if (response.ok) {
    const responseJson = await response.json();
    return responseJson;
  }
  // if response not ok then throw response text (error message)
  throw await createErrorResponse(response);
};

const defaultArgsModifier = (args) => args;

const productDucksArgsModifier = (args) => {
  if (args.body) {
    return args;
  }
  const { origin, ...argsWithoutOrigin } = args;
  const newArgs = {
    body: JSON.stringify(argsWithoutOrigin),
    headers: {},
  };
  if (!global.BROWSER && origin) {
    newArgs.headers.origin = origin;
  }
  return newArgs;
};

export const handleResponseWithStatus = (response) => {
  const textResponse = response.clone();
  const jsonResponse = response.clone();
  return jsonResponse
    .json()
    .catch(() => textResponse.text())
    .then((data) => ({ status: response.status, data }));
};

export const withStatusResponse = (procedureObject) => {
  return {
    ...procedureObject,
    call: (...args) =>
      procedureObject
        .call(merge.all([...args, { handleResponse: false }]))
        .then(handleResponseWithStatus),
  };
};

export const createOneDataProcedureConfig = (
  functionName,
  responseModifier = defaultResponseModifier,
  argsModifier = defaultArgsModifier
) => ({
  call: async ({
    fetchClient,
    args: inputArgs,
    getState,
    handleResponse = true,
  }) => {
    const args = argsModifier(inputArgs);
    const request = {
      method: 'POST',
      credentials: 'include',
      // prefer for a call to take slightly longer than to prematurely error out causing bad UX
      // can override if needed per call
      timeout: 30e3,
      ...(args.body ? { ...args } : { body: JSON.stringify(args) }),
    };
    request.headers = {
      'one-data-correlation-id': uuidv4(),
      'Content-Type': 'application/json',
      ...args.headers,
    };
    const oneDataApiUrl = getState().getIn(['config', 'oneDataUrl']);
    const response = await fetchClient(
      `${oneDataApiUrl}/${functionName}`,
      request
    );
    if (handleResponse) {
      return responseModifier(response);
    }
    return response;
  },
  getResultFromCache: ({ args, cache }) => {
    const hashedArgs = createCacheHash(argsModifier(args));
    if (!cache.has(hashedArgs)) {
      throw new Error('make the call');
    }
    return cache.get(hashedArgs);
  },
  buildUpdatedCache: ({ cache, args, result, error }) => {
    const hashedArgs = createCacheHash(argsModifier(args));

    return typeof result === 'undefined' && typeof error === 'undefined'
      ? cache.remove(hashedArgs)
      : cache.set(hashedArgs, error || result);
  },
});

// https://github.com/americanexpress/iguazu-rpc#setup
export const configureIguazuProcedures = () => {
  configureIguazuRPC({
    getToState: (state) =>
      state.getIn(['modules', 'banking-root', 'procedures']),
  });
  setProcedures({
    [CREATE_BANKING_SAVINGS_ACCOUNT_V1_FUNCTION]: createOneDataProcedureConfig(
      'CreateBankingSavingsAccount.v1'
    ),
    [READ_BANKING_FINANCIAL_INSTITUTION_LOOKUP_V1_FUNCTION]:
      createOneDataProcedureConfig('ReadBankingFinancialInstitutionLookup.v1'),
    [READ_BANKING_SAVINGS_ACCOUNTS_V2_FUNCTION]: {
      ...createOneDataProcedureConfig('ReadBankingSavingsAccounts.v2'),
      // https://github.com/americanexpress/iguazu-rpc#cachemodifier-optional
      cacheModifier:
        ({ cache }) =>
        ({ action, args }) => {
          switch (action) {
            case 'update': {
              const { accountToken, accountNickname } = JSON.parse(args.body);
              const updateAccount = (account) => {
                if (account.accountToken === accountToken) {
                  return {
                    ...account,
                    accountNickname,
                  };
                }
                return account;
              };
              const updateResponse = (response) => ({
                ...response,
                accounts: response.accounts.map(updateAccount),
              });
              return cache.map(updateResponse);
            }
            case 'clear':
              return cache.clear();
            default:
              return cache;
          }
        },
    },
    [READ_BUSINESS_CHECKING_APPLICATION_V1_FUNCTION]: {
      ...createOneDataProcedureConfig('ReadBusinessCheckingApplication.v1'),
      // https://github.com/americanexpress/iguazu-rpc#cachemodifier-optional
      cacheModifier:
        ({ cache }) =>
        (result) => {
          // id is always the same bc ReadBusinessCheckingAccountVendors does not take any args
          const id = createCacheHash({ body: JSON.stringify({}) });
          return cache.set(id, result);
        },
    },
    [READ_CUSTOMER_PRODUCTS_V2_FUNCTION]: createOneDataProcedureConfig(
      'ReadCustomerProducts.v2',
      defaultResponseModifier,
      productDucksArgsModifier
    ),
    [READ_USER_MFA_V1_FUNCTION]: createOneDataProcedureConfig(
      'ReadUserMultiFactorAuthentication.v1',
      defaultResponseModifier,
      productDucksArgsModifier
    ),
    [READ_CUSTOMER_PRODUCT_DETAILS_FUNCTION]: createOneDataProcedureConfig(
      'ReadCustomerProductDetails.v1',
      defaultResponseModifier,
      productDucksArgsModifier
    ),
    [READ_MEMBER_PREFERENCES_V1_FUNCTION]: createOneDataProcedureConfig(
      'ReadMemberPreferences.v1'
    ),
    [READ_USER_SESSION_V1_FUNCTION]:
      createOneDataProcedureConfig('ReadUserSession.v1'),
    [READ_BANKING_APPLICATION_DECISION_V1_FUNCTION]:
      createOneDataProcedureConfig('ReadBankingApplicationDecision.v1'),
    [CREATE_MEMBER_PREFERENCES_V1_FUNCTION]: createOneDataProcedureConfig(
      'CreateMemberPreferences.v1'
    ),
  });
};

export const configureIguazuResources = () => {
  configureIguazuREST({
    getToState: (state) =>
      state.getIn(['modules', 'banking-root', 'resources']),
    defaultOpts: {
      credentials: 'include',
    },
    composeFetch: createTimeoutFetch(6e3),
    resources: {
      [MEMBER_PRODUCTS_RESOURCE]: memberProducts,
      [MEMBER_PREFERENCES_RESOURCE]: memberPreferences,
      imageViewerDocUpload,
    },
  });
};
